import { AddressDto } from '@/types/AddressDto.ts';
import { MoneyValueDto } from '@/types/MoneyValueDto.ts';
import { PhoneNumberDto } from '@/types/PhoneNumberDto.ts';
import { FieldType } from '@/components/table/_fieldDefinitions/FieldType.ts';
import { BookingStatus } from '@/types/BookingStatus.ts';
import { BookingBalanceDto } from '@/features/dashboard/balances/types/BookingBalanceDto.ts';
import { PlannedTransactionDetailDto } from '@/features/dashboard/plannedTransactions/types/PlannedTransactionDetailDto.ts';
import { ParticipantTotalsDto } from '@/features/dashboard/bookings/types/BookingBillDto.ts';
import { BillField } from '@/features/dashboard/bookingChanges/types/dto/BillField.ts';
import { BillPaymentDto } from '@/features/dashboard/bookingChanges/types/dto/BillPaymentDto.ts';
import { ChangeItemType } from '@/features/dashboard/bookingChanges/types/dto/ChangeItemDto.ts';

/**
 * Rachunek rezerwacji złożony na froncie.
 *
 * **To nie jest kontrakt backendu i nigdy nim nie będzie.** Backend zna księgę
 * cen (`GET /bookings/{id}/bill`), dane osób (`GET /bookings/{id}`), wybory
 * uczestników (`GET /bookings/{id}/participant-selections`) i katalog
 * wyjazdu — ale nie zna sekcji ani kolejności wierszy, bo to decyzja
 * o wyglądzie ekranu. Sklejenie tych źródeł w sekcje
 * robi `BookingBillDto.mapper.ts`; stąd brak przyrostka `Dto` w nazwach.
 *
 * Kwoty niesie `MoneyValueDto`, daty — surowe ISO. Sumowanie pieniędzy należy
 * do backendu: `totals` i `totalsByParticipant` przychodzą z księgi gotowe.
 *
 * Kwota wiersza jest **listą po jednej pozycji na walutę** — rezerwacja bywa
 * wielowalutowa, a wiersz zbierający kilka pozycji księgi (opcja wielokrotnego
 * wyboru) potrafi objąć dwie waluty naraz. Pusta lista znaczy „wiersz bez
 * kwoty”, tak jak wcześniej `null`.
 */

/** Cena wariantu w jednym progu wiekowym; `null` po obu stronach = cena płaska. */
export interface BillVariantPrice {
  price: MoneyValueDto;
  minAge: number | null;
  maxAge: number | null;
}

/**
 * Wartość, którą wolno wybrać w wierszu: wariant opcji wyjazdu, wariant
 * transportu, ubezpieczenie albo opcja pola niestandardowego typu lista.
 * Wszystkie cztery są tym samym z punktu widzenia listy wyboru — nazwą pod
 * identyfikatorem, czasem z ceną.
 */
export interface BillVariant {
  id: string;
  name: string;
  /**
   * Cennik po progach wiekowych; pusty znaczy „ta wartość ceny nie ma”.
   * Ubezpieczenie nie ma jej nigdy (`Insurance` to nazwa i opis), opcja pola
   * niestandardowego tym bardziej.
   */
  prices: BillVariantPrice[];
  /**
   * Wycofana z oferty. Nie wchodzi do wyboru, ale zostaje w katalogu, bo wciąż
   * nazywa wartość bieżącą uczestnika, który ją ma.
   */
  archived: boolean;
}

/**
 * Katalog wyjazdu **pogrupowany tak, jak pyta o niego wiersz**: warianty jednej
 * opcji, warianty transportu, ubezpieczenia i opcje jednego pola listy.
 *
 * Grupowanie istnieje u źródła (`TourOptionDto.variants`,
 * `DropdownCustomFieldDefinitionDto.options`) i przechodzi tędy nietknięte —
 * płaski słownik `variantId → wariant` gubił je i kazał modalowi edycji
 * pokazywać przy warsztatach cały cennik rezerwacji.
 *
 * Wiersze stanu bieżącego katalogu nie potrzebują: niosą nazwy zamrożone
 * w księdze w chwili zakupu. Potrzebują go wyłącznie wartości **nowe** —
 * wskazane w otwartej paczce albo dopiero wybierane.
 */
export interface BillCatalog {
  /** `tourOptionId` → warianty tej opcji, w kolejności katalogu. */
  optionVariants: Record<string, BillVariant[]>;
  transportVariants: BillVariant[];
  insurances: BillVariant[];
  /**
   * Czy uczestnik musi mieć ubezpieczenie — `effectivelyRequired` oferty
   * wyjazdu, a nie zapisane `required`. Decyduje, czy wiersz ubezpieczenia
   * pozwala zrezygnować; backend odrzuca `InsuranceChangeOp.CLEAR` dokładnie
   * po tym samym pytaniu.
   */
  insuranceRequired: boolean;
  /** `customFieldId` → opcje listy rozwijanej z definicji pola. */
  customFieldOptions: Record<string, BillVariant[]>;
}

export interface BillLineRef {
  /**
   * Typ pozycji, którą wiersz przyjmie w paczce — ten sam dyskryminator, który
   * niesie `ChangeItemDto`, i ta sama nazwa, pod którą pozycja trafia do sekcji
   * `PatchBookingChangeRequest`.
   */
  kind: ChangeItemType;
  /** `OPTION` → `tourOptionId`, `CUSTOM_VALUES` → `customFieldId`, reszta → `null`. */
  targetId: string | null;
  /**
   * Pola kontraktu opisywane przez wiersz; wypełnione dla `PARTICIPANT_DATA`
   * i `CLIENT_DATA`, `null` dla pozostałych rodzajów.
   *
   * Lista, a nie pojedyncze pole, bo jeden wiersz rachunku bywa projekcją kilku
   * pól osoby: „Imię i nazwisko” to `FIRST_NAME` **i** `LAST_NAME`. Sklejone
   * pole `FULL_NAME` zmuszałoby formularz do zgadywania, gdzie kończy się imię
   * („Anna Maria Kowalska”), a kontrakt paczki i tak niesie oba osobno.
   */
  fields: BillField[] | null;
  /**
   * Aktualnie wybrana wartość — punkt odniesienia dla wartości „przed”, a przy
   * wyborze także jedyny powód, dla którego wariant wycofany z oferty wciąż
   * pojawia się na liście. Pole listy rozwijanej trzyma tu identyfikator swojej
   * opcji, bo `DropdownCustomFieldValueDto` niesie UUID, nie etykietę.
   */
  currentVariantId: string | null;
  /**
   * Wybrane warianty wiersza wielowartościowego („Wycieczki fakultatywne”);
   * `null` dla wierszy jednowartościowych, które opisuje `currentVariantId`.
   *
   * Bez tej listy wiersz zna wyłącznie **nazwy** wybranych wariantów (`chips`),
   * a pozycja paczki wymaga UUID-a (`removedVariantId`) — zmiany takiego wiersza
   * nie dałoby się złożyć bez zgadywania po etykiecie.
   */
  currentVariantIds: string[] | null;
}

/** Pojedynczy wiersz rachunku w stanie bieżącym. */
export interface BillLine {
  id: string;
  /**
   * Pozycje księgi, z których wiersz się składa — po nich wiąże się go
   * z deltami podglądu paczki zwykłym złączeniem po `id`.
   *
   * Zwykle zero albo jedna; wiersz wielowartościowy („Wycieczki fakultatywne”)
   * zbiera po jednej na wybrany wariant, bo tak trzyma je księga.
   */
  costLineIds: string[];
  /**
   * Tożsamość wiersza w księdze (`costRowKey`) — dla pozycji, którą paczka
   * dopiero **doda**: takiej podgląd nie daje żadnego identyfikatora, więc
   * złączenie po `id` nie ma czego złączyć. `null` dla wierszy, które pozycją
   * rachunku nie są (dane osoby, pole niestandardowe, ubezpieczenie).
   */
  costKey: string | null;
  /**
   * Po czym mapper poznaje, że pozycja szkicu dotyczy tego wiersza.
   *
   * `null` znaczy, że paczka nie ma dla wiersza żadnego typu pozycji, więc
   * zmienić go nie sposób: zniżki i korekty rachunku (dziś poza modelem paczek
   * zmian, sterowane spoza tego modułu) oraz pola tylko do odczytu.
   * To co innego niż `locked` — tam wiersz da się ruszyć, tylko na innym ekranie.
   */
  ref: BillLineRef | null;
  label: string;
  /**
   * Rodzaj kontrolki, którą wiersz dostaje w modalu edycji. Wiadomy
   * z konstrukcji, a nie zgadywany: pole predefiniowane ma typ z definicji
   * wiersza, pole niestandardowe — z `CustomFieldDefinitionDto.type`, a opcja,
   * transport i ubezpieczenie to zawsze wybór wariantu.
   */
  type: FieldType;
  value: string | null;
  /** Wartość wielokrotna; wyklucza się z `value`. */
  chips: string[] | null;
  /** Kwoty pozycji księgi zebrane po walutach; pusta lista = wiersz bez kwoty. */
  amount: MoneyValueDto[];
  /** Tekst zamiast kwoty, np. „w cenie”, „—”. */
  amountLabel: string | null;
  amountMuted: boolean;
  /** Wartość wyszarzona, np. „Nie wybrano”. */
  muted: boolean;
  /** Pozycja sterowana z innego ekranu (np. zniżka całej rezerwacji). */
  locked: boolean;
  /** Wolna linia operatora. */
  manual: boolean;
  /**
   * Kwota ustalona ręcznie przez operatora, a nie wzięta z cennika — znacznik ✎
   * przy wierszu.
   */
  priceOverridden: boolean;
  /** Uzasadnienie ręcznej kwoty; wypełnione tylko przy `priceOverridden`. */
  priceNote: string | null;
  /** ISO; data wyboru bieżącej wartości — dziś tylko wiersz ubezpieczenia. */
  selectedAt?: string;
}

/** Zwijana sekcja rachunku — odpowiednik `BillSectionVm`, ale bez stanu szkicu. */
export interface BillSection {
  id: string;
  title: string;
  hint: string | null;
  note: string | null;
  /** Sekcja przyjmuje wolne linie operatora („+ Wolna linia”). */
  allowManualLines: boolean;
  /** Sekcja niesie kwoty — sekcje zapisującego nie niosą. */
  withAmounts: boolean;
  lines: BillLine[];
}

/** Rachunek jednego uczestnika: dane osoby, jej wybory i przypadające na nią wiersze księgi. */
export interface ParticipantBill {
  participantId: string;
  firstName: string;
  lastName: string;
  birthDate: string | null;
  /** Wiek na pierwszy dzień turnusu — jedyny, od którego cokolwiek zależy. */
  age: number | null;
  /** Uczestnik po rezygnacji zostaje w rachunku, ale bez pozycji. */
  withdrawn: boolean;
  sections: BillSection[];
}

/** Dane osoby zapisującej w kontekście tej rezerwacji. */
export interface ClientBill {
  clientId: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: PhoneNumberDto | null;
  address: AddressDto;
  /** Sekcje bez kolumny kwot — zapisujący własnego rachunku nie ma. */
  sections: BillSection[];
}

export interface BookingBill {
  bookingId: string;
  /** Kod widoczny dla operatora, np. `REZ-2026-0431`. */
  bookingCode: string;
  tourId: string;
  tourName: string;
  /** `YYYY-MM-DD`. */
  tourStartDate: string;
  tourEndDate: string;
  status: BookingStatus;
  client: ClientBill;
  participants: ParticipantBill[];
  /** Pozycje dotyczące rezerwacji jako całości — wiersze `MANUAL` bez uczestnika. */
  bookingWideSections: BillSection[];
  installments: PlannedTransactionDetailDto[];
  payments: BillPaymentDto[];
  /** Saldo per waluta: „wpłacono” i „pozostało” liczy backend, nie front. */
  balances: BookingBalanceDto[];
  /** Wartość rezerwacji netto z księgi, po jednej pozycji na walutę. */
  totals: MoneyValueDto[];
  totalsByParticipant: ParticipantTotalsDto[];
  catalog: BillCatalog;
}

/** Czy wiersz opisuje dane pole osoby. */
export const hasBillField = (
  ref: BillLineRef | null,
  field: BillField,
): boolean => ref?.fields?.includes(field) ?? false;

/** Pola osoby opisywane przez wiersz; pusta lista dla wierszy spoza tej grupy. */
export const billFieldsOf = (ref: BillLineRef | null): BillField[] =>
  ref?.fields ?? [];

/**
 * Wartości dopuszczalne dla wiersza — grupa katalogu wskazana jego odsyłaczem.
 * Pusta lista znaczy „ten wiersz nie wybiera z katalogu” (dane osoby, cena
 * podstawowa, pozycja ręczna) albo „wyjazd nie ma tu czego zaoferować”.
 */
export const catalogVariantsOf = (
  catalog: BillCatalog,
  ref: BillLineRef | null,
): BillVariant[] => {
  switch (ref?.kind) {
    case ChangeItemType.OPTION:
      return ref.targetId ? (catalog.optionVariants[ref.targetId] ?? []) : [];
    case ChangeItemType.TRANSPORT:
      return catalog.transportVariants;
    case ChangeItemType.INSURANCE:
      return catalog.insurances;
    case ChangeItemType.CUSTOM_VALUES:
      return ref.targetId
        ? (catalog.customFieldOptions[ref.targetId] ?? [])
        : [];
    default:
      return [];
  }
};

/** Wartość katalogu po identyfikatorze, szukana w grupie właściwej dla wiersza. */
export const findCatalogVariant = (
  catalog: BillCatalog,
  ref: BillLineRef | null,
  variantId: string | null,
): BillVariant | null =>
  variantId
    ? (catalogVariantsOf(catalog, ref).find(
        (variant) => variant.id === variantId,
      ) ?? null)
    : null;

/**
 * Cena wariantu dla uczestnika w danym wieku.
 *
 * Cennik trzyma po jednej cenie na próg wiekowy, a wiek uczestnika jest znany
 * (`ParticipantBill.age`, liczony na pierwszy dzień turnusu — od niego zależą
 * progi), więc próg wybiera się, a nie zgaduje. `null` w wieku (brak daty
 * urodzenia) i wiek poza wszystkimi progami spadają na pierwszą pozycję
 * cennika: to podpowiedź w etykiecie listy, a nie kwota, którą cokolwiek się
 * księguje — tę wylicza backend przy zapisie pozycji.
 */
export const variantPriceForAge = (
  variant: BillVariant,
  age: number | null,
): MoneyValueDto | null => {
  const matching =
    age === null
      ? undefined
      : variant.prices.find(
          ({ minAge, maxAge }) =>
            (minAge === null || age >= minAge) &&
            (maxAge === null || age <= maxAge),
        );

  return (matching ?? variant.prices[0])?.price ?? null;
};
